import { useAssistantState } from '@assistant-ui/react'
import {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { extractSourcesFromMessage } from '#app/components/assistant-ui/message-sources.js'
import { Separator } from '#app/components/ui/separator'
import { extractSlug } from '#app/utils/misc.js'
import { SingleAd, type SafeAd } from '../ads-v2'

type AdContextValue = {
	ad: SafeAd | null
	setAd: (ad: SafeAd | null) => void
}

const AdContext = createContext<AdContextValue>({
	ad: null,
	setAd: () => {},
})

export function AdProvider({ children }: { children: React.ReactNode }) {
	const [ad, setAd] = useState<SafeAd | null>(null)
	const value = useMemo(() => ({ ad, setAd }), [ad])
	return <AdContext.Provider value={value}>{children}</AdContext.Provider>
}

/**
 * Fetches an ad based on the current assistant message's sources.
 * Stores the ad in local state (for inline rendering) and updates
 * the shared AdContext (so SideBarAd can display the latest ad).
 */
export function InThreadAd() {
	const sources = useAssistantState(({ message }) =>
		extractSourcesFromMessage(
			message.metadata as Record<string, unknown> | undefined,
		),
	)

	const { setAd: setContextAd } = useContext(AdContext)
	// Local state so this instance keeps showing its fetched ad even if the
	// context value changes later (e.g. a newer message fetches a different ad).
	const [ad, setAd] = useState<SafeAd | null>(null)
	const fetchedRef = useRef(false)

	useEffect(() => {
		if (!sources || sources.length === 0 || fetchedRef.current) return

		const sourceUrl = sources[0]?.url || ''
		const slug = extractSlug(sourceUrl)
		if (!slug) return

		fetchedRef.current = true
		const controller = new AbortController()

		fetch(`/resources/get-ad/${encodeURIComponent(slug)}`, {
			signal: controller.signal,
		})
			.then(response => {
				if (!response.ok) {
					console.error('Ad fetch failed', response.status, slug)
					return null
				}

				return response.json() as Promise<SafeAd>
			})
			.then(fetchedAd => {
				if (fetchedAd) {
					setAd(fetchedAd)
					setContextAd(fetchedAd)
				}
			})
			.catch(error => {
				if (error.name !== 'AbortError') {
					fetchedRef.current = false
					console.error('Failed to fetch ad:', error)
				}
			})

		return () => controller.abort()
	}, [sources, setContextAd])

	if (!ad) {
		return null
	}

	return (
		<div className="min-[1344px]:hidden">
			<Separator className="my-3" />
			<SingleAd
				ad={ad}
				className="mx-auto hidden w-[400px] sm:block md:w-[684px] lg:mt-4"
				imageSizes={[400, 684, 1024]}
				sizes="(max-width: 400px) 400px, 684px"
				supportedFormats={['landscape']}
			/>
			<SingleAd
				ad={ad}
				className="mx-auto my-4 block sm:hidden"
				imageSizes={[400]}
				sizes="400px"
				supportedFormats={['square', 'landscape']}
			/>
		</div>
	)
}

export function SideBarAd() {
	const { ad } = useContext(AdContext)
	const isEmpty = useAssistantState(({ thread }) => thread.isEmpty)

	// Reserve the field as soon as the conversation starts (first message sent),
	// not just when the ad finishes loading — this way the chat shifts once, in
	// step with the field appearing, instead of jumping again when a late ad
	// pops in. Stays hidden on the empty start page. Kept in sync with `showRail`
	// in thread.tsx (both gate on a non-empty thread).
	if (isEmpty) {
		return null
	}

	return (
		// Field widths (280/320px) and breakpoints (1344/1700px) must stay in
		// sync with the chat-shift calc in thread.tsx (the `--adw` var + 1088px
		// constant) — otherwise the chat can overlap or leave a gap to this field.
		<aside className="fixed right-0 top-16 hidden h-[calc(100%-4rem)] w-[280px] overflow-y-auto overscroll-contain border-l bg-[#f5f8f1] p-4 min-[1344px]:block min-[1700px]:w-[320px] dark:bg-muted">
			{ad && (
				<SingleAd
					ad={ad}
					className="w-full"
					imageSizes={[280, 320, 400, 640]}
					sizes="(min-width: 1700px) 320px, 280px"
					supportedFormats={['portrait', 'square', 'landscape']}
				/>
			)}
		</aside>
	)
}
